import React, { useEffect, useState, useMemo } from "react";
import { apiFetch } from "./api";
import { formatData } from "../utils/expirare";

const FILTRE = [
  { id: "toate", text: "Toate" },
  { id: "programata", text: "Programate" },
  { id: "efectuata", text: "Efectuate" },
];

const TIPURI = {
  service: "Service / Revizie",
  itp: "ITP",
  revizie: "Revizie",
  anvelope: "Anvelope",
  altele: "Altele",
};

const STATUS_TEXT = {
  programata: "Programată",
  efectuata: "Efectuată",
  anulata: "Anulată",
};

export default function Programari() {
  const [programari, setProgramari] = useState([]);
  const [stare, setStare] = useState("loading");
  const [filtru, setFiltru] = useState("toate");
  const [confirmand, setConfirmand] = useState(null); // id-ul programării în curs de confirmare

  const incarca = () => {
    apiFetch("/programari")
      .then((data) => {
        setProgramari(data.programari || []);
        setStare("ok");
      })
      .catch(() => setStare("error"));
  };
  useEffect(incarca, []);

  // v9.0: „double check" — clientul confirmă că mașina a ajuns la service.
  async function confirma(p) {
    const sigur = window.confirm(
      `Confirmi că programarea pentru ${p.nr_inmatriculare} din ${formatData(p.data_prog)} a fost efectuată (mașina a ajuns la service)?`
    );
    if (!sigur) return;
    setConfirmand(p.id);
    try {
      const d = await apiFetch(`/programari/${p.id}/confirma`, { method: "POST" });
      if (d && d.ok) incarca();
      else alert((d && d.message) || "Nu am putut confirma. Reîncearcă.");
    } catch (e) {
      alert(e.message || "Nu am putut confirma. Reîncearcă.");
    } finally {
      setConfirmand(null);
    }
  }

  const afisate = useMemo(() => {
    if (filtru === "toate") return programari;
    return programari.filter((p) => p.status === filtru);
  }, [programari, filtru]);

  const nrViitoare = useMemo(
    () => programari.filter((p) => p.status === "programata").length,
    [programari]
  );

  return (
    <div className="programari">
      <div className="dash__head">
        <h1>Programări service / ITP</h1>
        <p>
          Aici vezi când trebuie să duci mașinile la service sau ITP. Programările sunt făcute de
          echipa LEASEIN. După ce mașina a fost dusă, te rugăm să apeși „Confirmă efectuarea" —
          așa știm sigur că totul e în regulă.
        </p>
      </div>

      {stare === "loading" && <SkeletonProg />}

      {stare === "error" && (
        <div className="portal__loading">
          Nu am putut încărca programările. Reîncearcă mai târziu.
        </div>
      )}

      {stare === "ok" && programari.length === 0 && (
        <div className="portal__loading">
          Nu ai nicio programare momentan. Vor apărea aici imediat ce echipa LEASEIN face o programare.
        </div>
      )}

      {stare === "ok" && programari.length > 0 && (
        <>
          {nrViitoare > 0 && (
            <div className="facturi__sumar">
              <span className="dot dot--galben" />
              Ai <strong>{nrViitoare}</strong>{" "}
              {nrViitoare === 1 ? "programare activă" : "programări active"} la service/ITP.
            </div>
          )}

          <div className="chips">
            {FILTRE.map((f) => (
              <button
                key={f.id}
                className={`chip ${filtru === f.id ? "chip--active" : ""}`}
                onClick={() => setFiltru(f.id)}
              >
                {f.text}
              </button>
            ))}
          </div>

          <p className="masini__count">
            {afisate.length} {afisate.length === 1 ? "programare" : "programări"}
            {filtru !== "toate" ? ` (din ${programari.length})` : ""}
          </p>

          {afisate.length === 0 ? (
            <div className="portal__loading">Nicio programare în această categorie.</div>
          ) : (
            <div className="prog__grid" key={filtru}>
              {afisate.map((p) => (
                <ProgCard key={p.id} p={p} onConfirma={confirma} confirmand={confirmand} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProgCard({ p, onConfirma, confirmand }) {
  return (
    <div className={`prog-card prog-card--${p.status}`}>
      <div className="prog-card__top">
        <span className="prog-card__tip">{TIPURI[p.tip_lucrare] || p.tip_lucrare}</span>
        <span className={`pill ${p.status === "efectuata" ? "pill--verde" : p.status === "anulata" ? "pill--gri" : "pill--albastru"}`}>
          {STATUS_TEXT[p.status] || p.status}
        </span>
      </div>
      <div className="prog-card__data">
        {formatData(p.data_prog)}{p.ora_prog ? `, ora ${p.ora_prog}` : ""}
      </div>
      <div className="prog-card__masina">
        {p.nr_inmatriculare}
        {p.marca_model ? <span className="prog-card__model"> · {p.marca_model}</span> : null}
      </div>
      {p.locatie && <div className="prog-card__loc">📍 {p.locatie}</div>}
      {p.note && <div className="prog-card__note">{p.note}</div>}

      {p.status === "programata" && (
        <button
          className="btn btn--primary prog-card__confirma"
          onClick={() => onConfirma(p)}
          disabled={confirmand === p.id}
        >
          {confirmand === p.id ? "Se confirmă…" : "✓ Confirmă efectuarea"}
        </button>
      )}
      {p.status === "efectuata" && p.confirmataClient && (
        <div className="prog-card__confirmata">
          ✓ Confirmată de tine{p.confirmataLa ? ` · ${formatData(p.confirmataLa)}` : ""}
        </div>
      )}
    </div>
  );
}

function SkeletonProg() {
  return (
    <div className="prog__grid" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div className="prog-card" key={i}>
          <span className="skel skel--line" style={{ width: "40%", height: 14 }} />
          <span className="skel skel--line" style={{ width: "70%", height: 22, marginTop: 12 }} />
          <span className="skel skel--line" style={{ width: "55%", height: 14, marginTop: 12 }} />
        </div>
      ))}
    </div>
  );
}
